import type { ReactNode } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { FieldTooltip } from "@/shared/components/form/field-tooltip";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Textarea } from "@/shared/components/ui/textarea";
import { cn } from "@/shared/lib/cn";

interface TextareaFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: FieldPath<TValues>;
  label: string;
  placeholder?: string;
  hint?: string;
  tooltip?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  rows?: number;
  /// Cap on character count. When set, a [UI-L3] contextual counter appears
  /// within 200 chars of the cap and turns red past the limit.
  maxLength?: number;
  /// Distance from cap to show the counter. Default 200 chars.
  counterThreshold?: number;
  className?: string;
  textareaClassName?: string;
  /// Optional action rendered in the label row, right side (after the
  /// counter). Used by the writing assistant's sparkle button.
  labelAction?: ReactNode;
  /// Optional node rendered absolutely over the textarea (e.g. the writing
  /// assistant's shimmer). The textarea wrapper becomes position-relative
  /// when set.
  overlay?: ReactNode;
}

/// Multi-line textarea wired to react-hook-form. Built-in [UI-L3] contextual
/// counter and [UI-L4] max-h-40 + overflow-y-auto so long messages cap
/// growth and scroll past the threshold.
export function TextareaField<TValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  hint,
  tooltip,
  disabled,
  autoFocus,
  rows = 5,
  maxLength,
  counterThreshold = 200,
  className,
  textareaClassName,
  labelAction,
  overlay,
}: TextareaFieldProps<TValues>): ReactNode {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const value = (field.value ?? "") as string;
        const len = value.length;
        const overLimit = maxLength !== undefined && len > maxLength;
        const showCounter = maxLength !== undefined && len >= maxLength - counterThreshold;

        const counter =
          showCounter && maxLength !== undefined ? (
            <span className={cn("text-2xs", overLimit ? "text-destructive" : "text-text-tertiary")}>
              {len} / {maxLength}
            </span>
          ) : null;

        return (
          <FormItem className={className}>
            <div className="flex items-baseline justify-between">
              <div className="flex items-center gap-1.5">
                <FormLabel>{label}</FormLabel>
                {tooltip ? <FieldTooltip term={label} content={tooltip} /> : null}
              </div>
              {/* Group counter and optional label action so they stay right-aligned together. */}
              <div className="flex items-center gap-2">
                {counter}
                {labelAction}
              </div>
            </div>
            {/*
              The positioning wrapper sits OUTSIDE FormControl, not inside it.
              FormControl is a Slot that merges `id`, `aria-describedby` and
              `aria-invalid` onto its single direct child. With the div inside,
              every one of those landed on the div: the textarea got no id, so
              FormLabel's htmlFor pointed at a div and the control had no
              accessible name at all, in all 30 files that use this field.
              Caught by an e2e step that could not find a textarea by its own
              visible label.
            */}
            <div className="relative">
              <FormControl>
                <Textarea
                  placeholder={placeholder}
                  disabled={disabled}
                  autoFocus={autoFocus}
                  rows={rows}
                  className={cn("max-h-40 overflow-y-auto", textareaClassName)}
                  {...field}
                  value={value}
                />
              </FormControl>
              {overlay}
            </div>
            {hint ? <FormDescription>{hint}</FormDescription> : null}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
